<script lang="ts">
    import { createEventDispatcher } from "svelte";
    import { TopicAccessLevel } from "$/stores/extension/ActiveTopicStore";
    import DeleteCommentDialog from "./DeleteCommentDialog.svelte";
    import Editor from "$/components/editor/Editor.svelte";
    import { tooltip } from "$/utils/tooltip";
    import { clickOutside } from "$/utils/clickOutside.js";
    import { formattedDateAndTime, fromNow } from "../utils/date";
    import { highlightTarget } from "../utils/highlightTarget";
    import { clickAction } from "$/utils/clickAction";
    import { trimHTMLText } from "$/utils/Utils";
    import { CommentMentions } from "$/utils/CommentMentions";
    import { type Comment } from "$/models/Comment";
    import { type User } from "$/models/User";
    import { AppStore, focusedCommentId } from "$/stores/AppStore";

    export let comment: Comment = {};
    export let user: User;
    export let editRole: number;
    export let reverseSuggestionList: boolean;

    let editorReferenceElement: HTMLElement;
    let trayOpen: boolean = false;
    let editorOpen: boolean = false;
    let showDeleteDialog: boolean = false;
    let commentInputValue: string = "";
    let commentTitle: string = "";
    let mentions: CommentMentions;

    $: mentionsWithRole = mentions ? mentions.getMentionsWithRole() : [];

    $: {
        let dateFormatted = formattedDateAndTime(comment.createdOn);

        if (comment.createdBy)
            commentTitle = `${comment.createdBy.firstName} ${comment.createdBy.lastName} - ${dateFormatted}`;
        else commentTitle = `${dateFormatted}`;
    }

    const dispatch = createEventDispatcher();

    const toggleTray = () => {
        trayOpen = !trayOpen;
    };

    const showEditor = () => {
        trayOpen = false;
        editorOpen = true;
        commentInputValue = comment.comment;
    };

    const openDeleteDialog = () => {
        trayOpen = false;
        showDeleteDialog = true;
        editorOpen = false;
    };

    const cancelUpdate = () => {
        editorOpen = false;
        mentions.clear();
    };

    const confirmUpdate = () => {
        if (trimHTMLText(commentInputValue) === "") {
            commentInputValue = comment.comment;
            editorOpen = false;
            return;
        }
        comment.comment = commentInputValue;
        comment.mentionMembers = mentions.getMentionedMembers();
        comment.mentionAndCreateMembers = mentions.getCreateAndMentionMembers();
        comment.mentionNonMembers = mentions.getMentionedNonMembers();

        editorOpen = false;
        mentions.clear();
        dispatch("update", comment);
    };

    const reply = () => {
        trayOpen = false;
        dispatch("reply", comment);
    };
</script>

<div
    use:highlightTarget={{
        nodeId: comment?.id,
        targetId: $focusedCommentId,
        callback: () => {
            if ($focusedCommentId === comment?.id) {
                AppStore.setFocused(null);
            }
        },
    }}
    bind:this={editorReferenceElement}
    data-reversesuggestionlist={reverseSuggestionList}
    class="comment"
    class:comment-editor={editorOpen === true}
    class:z-10={trayOpen === true}
>
    <!-- span is a hack for node.insertBefore error on comment click -->
    <span>
        <div
            class="comment-header"
            class:comment-edit-title={editorOpen === true}
        >
            <span class="comment-header__title" use:tooltip={commentTitle}>
                {#if comment.createdBy}
                    {comment.createdBy.firstName}
                    {comment.createdBy.lastName} &nbsp-&nbsp;
                {/if}

                {fromNow(comment.createdOn)}
            </span>

            {#if editRole >= TopicAccessLevel.COMMENTER}
                <div class="comment-options">
                    <div
                        class="comment-header__button"
                        use:clickAction={toggleTray}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class=""
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            stroke-width="2"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M5 12h.01M12 12h.01M19 12h.01M6 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z"
                            />
                        </svg>
                    </div>
                </div>
            {/if}
        </div>

        {#if trayOpen}
            <div
                use:clickOutside
                on:click_outside={() => (trayOpen = false)}
                class="comment-tray"
            >
                <div class="comment-tray__item" use:clickAction={reply}>
                    Reply
                </div>
                {#if user?.id === comment?.createdBy?.id}
                    <div
                        class="comment-tray__item"
                        use:clickAction={showEditor}
                    >
                        Edit
                    </div>
                    <div
                        class="comment-tray__item"
                        use:clickAction={openDeleteDialog}
                    >
                        Delete
                    </div>
                {/if}
            </div>
        {/if}
    </span>
    {#if !editorOpen}
        <div class="comment-content">
            {@html comment.comment}
        </div>
    {:else}
        <Editor
            bind:editorInputValue={commentInputValue}
            bind:mentions
            content={comment.comment}
            action={confirmUpdate}
            userRole={editRole}
            parentWrapper={editorReferenceElement}
        />
        <div class="cw-btn-container">
            <button class="btn right" on:click={cancelUpdate}>Cancel</button>
            <button class="btn" on:click={confirmUpdate}
                >{editRole >= TopicAccessLevel.EDITOR && mentionsWithRole.length
                    ? "Save & Share"
                    : "Save"}</button
            >
        </div>
    {/if}

    {#if showDeleteDialog}
        <DeleteCommentDialog
            on:deletionConfirmed={() => {
                dispatch("delete");
                showDeleteDialog = false;
            }}
            on:deletionCanceled={() => {
                showDeleteDialog = false;
            }}
        />
    {/if}
</div>

<style>
    @import "../main.css";

    .comment {
        @apply reset /** NOTE: This is needed for highlight effect */ border-0 border-b-[1px] border-solid border-gray-200 bg-gray-100 px-4 py-2 text-sm last:rounded-b-xl last:border-b-0 !important;
    }

    :global(.comment:last-child .dialog) {
        @apply rounded-b-xl !important;
    }

    .comment-editor {
        @apply bg-white px-0 py-2 last:rounded-b-xl !important;
    }

    .comment-tray {
        @apply reset absolute right-4 top-1 z-50 flex flex-col rounded-md border border-solid border-gray-200 bg-white p-1 shadow-sm !important;
    }

    .comment-tray__item {
        @apply reset flex cursor-pointer items-center justify-start rounded px-2 py-1 text-xs hover:bg-gray-200 !important;
    }

    .comment-header {
        @apply reset flex w-full flex-row pb-1 !important;
    }

    .comment-header__title {
        @apply reset text-xs text-gray-400 hover:text-gray-700 !important;
    }

    .comment-options {
        @apply ml-auto !important;
    }

    .comment-header__button {
        @apply reset ml-auto flex h-4 cursor-pointer !important;
    }

    .comment-header__button svg {
        @apply reset h-4 w-4 text-gray-400 hover:text-gray-700 !important;
    }

    .comment-content {
        @apply reset w-full max-w-full overflow-x-hidden overflow-ellipsis break-words text-sm px-0.5 -mx-0.5 !important;
    }

    /* purgecss start ignore */
    :global(.comment-content img) {
        @apply h-auto max-w-full rounded-lg !important;
    }

    :global(.comment-content ul) {
        @apply list-inside list-disc !important;
    }
    :global(.comment-content ol) {
        @apply list-inside list-decimal !important;
    }
    :global(.comment-content li::marker) {
        @apply text-sm !important;
    }
    :global(.comment-content li p) {
        @apply inline text-sm !important;
    }
    :global(.comment-content p:empty:before) {
        content: " " !important;
        white-space: pre !important;
    }

    :global(.comment-content a), :global(.comment-content a > *) {
        @apply text-pink-500 !important;
    }
 
    /*
    * NOTE: There are 2 options for suggestions in comments we'll remove one after some UI testing
    */

    /* OPTION 1: Plain text with hover effect */
    :global(.comment-content cw-mention) {
        @apply editor-suggestion border-transparent hover:border-gray-300 hover:bg-gray-200 -mx-0.5 font-medium !important;
    }
    :global(.comment-content cw-tag) {
        @apply editor-suggestion border-transparent hover:bg-sky-100 hover:border-sky-200 -mx-0.5 font-medium !important;
        word-break: break-word;
        overflow-wrap: anywhere;
    }
    
    /* OPTION 2: Label effect */
    /* :global(.comment-content cw-mention) {
        @apply editor-suggestion border-gray-300 bg-gray-200 hover:bg-gray-300 hover:border-gray-400 !important;
    }
    :global(.comment-content cw-tag) {
        @apply editor-suggestion border-gray-300 bg-gray-200 hover:bg-sky-100 hover:border-sky-200 !important;
        word-break: break-word;
        overflow-wrap: anywhere;
    } */

    :global(.comment-content cw-tag span) {
        @apply hover:text-sky-600 !important;
    }
    /* purgecss end ignore */

    .cw-btn-container {
        @apply flex select-none border-0 border-solid px-4 py-0 !important;
    }

    .btn {
        @apply reset rounded border-none px-2 py-1 text-xs font-normal text-black no-underline hover:bg-gray-200 focus:outline-none !important;
    }

    .btn.right {
        @apply ml-auto !important;
    }

    .comment-edit-title {
        @apply px-4 py-0 !important;
    }
</style>
